<script setup lang="ts">
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { AppBackButton } from '@libs/ui';

import FirstDayTripPriceRibbonMobile from '../../../core/components/FirstDayTripPriceRibbonMobile.vue';
import { useRouterStore, useStatsigEventsStore } from '../../../core/stores';
import { ShowPriceRibbonButton } from '../../../modules/priceRibbon';
import {
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';
import { HotelFiltersButtons } from '../../../modules/hotelFilters';

import HotelSelectionDestination from './HotelSelectionDestination.vue';

const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();

interface HotelSelectionHeaderMobileEmits {
  (event: 'show-price-ribbon'): void;
}
const emit = defineEmits<HotelSelectionHeaderMobileEmits>();

function onBackButtonClick(): void {
  routerStore.navigateToTripOverview();
  statsigEventsStore.logHotelSelectionBackButtonClicked();
}

function onNavigationTriggered(): void {
  statsigEventsStore.logHotelSelectionBackButtonClicked();
}
</script>

<template>
  <div class="hotel-selection-header-mobile">
    <FirstDayTripPriceRibbonMobile
      :show-month-select-dropdown="true"
      :show-price-ribbon="hotelSelectionStore.shouldShowPriceRibbon"
      :source="CoreEventSource.HotelSelection"
      :shadow="true"
    >
      <template #left-action>
        <AppBackButton
          type="black"
          :text="$t('planner.back')"
          :link="routerStore.getTripOverviewUrl()"
          font="font-label-l-light"
          @click="onBackButtonClick"
          @navigation-triggered="onNavigationTriggered"
        />
      </template>
      <template #right-action>
        <ShowPriceRibbonButton
          class="show-price-ribbon-button"
          @click="emit('show-price-ribbon')"
        />
      </template>
      <template #bottom-row>
        <div class="bottom-row">
          <div class="actions-section">
            <HotelSelectionDestination
              v-if="hotelSelectionStore.hotelSelectionHeaderData"
              class="hotel-selection-destination"
              :data="hotelSelectionStore.hotelSelectionHeaderData"
              @open-filters="hotelSelectionStore.openFilters()"
              @change-number-of-nights="
                hotelSelectionStore.changeNumberOfNights
              "
              @change-destination="
                hotelSelectionModalsStore.openDestinationSelectorModal(
                  $event,
                  hotelSelectionStore.editingIngredientId
                )
              "
            />
            <HotelFiltersButtons
              :suggestion-type="hotelSelectionStore.listSuggestionType"
              :board-basis-options="hotelSelectionStore.hotelMealFilterOptions"
              :amenities-options="
                hotelSelectionStore.hotelAmenitiesFilterOptions
              "
              :accommodation-type-options="
                hotelSelectionStore.accommodationTypeFilterOptions
              "
              :ingredient="hotelSelectionStore.editingStayIngredient"
              @open-all-filters="hotelSelectionStore.openFilters()"
              @open-why-we-suggest-modal="
                hotelSelectionModalsStore.openWhyWeSuggestModal
              "
              @suggestion-type-selected="
                hotelSelectionStore.setListSuggestionType
              "
            />
          </div>
        </div>
      </template>
    </FirstDayTripPriceRibbonMobile>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-header-mobile {
  position: sticky;
  top: 0;
  z-index: 3;

  .show-price-ribbon-button {
    color: var(--color-secondary-40);
  }

  .bottom-row {
    .actions-section {
      background-color: var(--color-white);
      padding: var(--spacing-12) 0 var(--spacing-8) var(--spacing-16);
      gap: var(--spacing-12);
      display: flex;
      flex-direction: column;
      justify-content: center;
      .hotel-selection-destination {
        padding-right: var(--spacing-16);
      }
    }
  }
}
</style>
